import type {
  AdRuleType,
  AstAdObj,
  AstApnTag,
  AstKeywordValue,
  EidsUserSource,
  RelevantYield,
  ViewType,
} from "@omni/ads";

import { getPulseTracker } from "../../../../pulse";
import { PPID3CacheHandler } from "../../../../services/PPID3/util/cacheHandler";
import { layoutNames, sharedVars, specialCharacters } from "../constants";

// SDK polling configuration
const MAX_SDK_RETRIES = 20; // 20 * 50ms = 1 second max wait
const SDK_POLL_INTERVAL = 50; // milliseconds

/**
 * Generic SDK poller with promise caching
 * Polls for an SDK to be available on window object, with retry logic and promise sharing
 */
function createSDKPoller<T>(
  sdkName: string,
  getSDK: () => T | undefined,
  options: { allowSSR?: boolean } = {},
) {
  let cachedPromise: Promise<T> | null = null;

  return function poll(retries: number = 0): Promise<T> {
    // Return cached promise if polling is already in progress
    if (cachedPromise) {
      return cachedPromise;
    }

    const promise = new Promise<T>((resolve, reject) => {
      // Handle SSR/test environments
      if (typeof window === "undefined") {
        if (options.allowSSR) {
          cachedPromise = null;
          resolve(undefined as any);
          return;
        }
        cachedPromise = null;
        reject(new Error(`${sdkName}: window is not defined`));
        return;
      }

      // Check if SDK is available
      const sdk = getSDK();
      if (sdk) {
        cachedPromise = null;
        resolve(sdk);
        return;
      }

      // Max retries reached
      if (retries >= MAX_SDK_RETRIES) {
        const errorMsg = `${sdkName}: Max retries reached, SDK may not have loaded`;
        console.error(errorMsg);
        cachedPromise = null;
        reject(new Error(errorMsg));
        return;
      }

      // Retry after interval
      setTimeout(() => {
        cachedPromise = null;
        resolve(poll(retries + 1));
      }, SDK_POLL_INTERVAL);
    });

    cachedPromise = promise;
    return promise;
  };
}

// Create SDK pollers using the generic helper
const getBiddingSdk = createSDKPoller<RelevantYield>(
  "getBiddingSdk",
  () => window?.relevantDigital,
);

/**
 * Helper to transform PPID source to EID format
 * Reduces duplication in getUserIds function
 */
const transformPPIDToEid = (
  ppIds: EidsUserSource[],
  sourcePattern: string,
  targetSource: string,
) => {
  const matchingPpid = ppIds.find((ppid) => ppid.source === sourcePattern);

  if (!matchingPpid) return null;

  return {
    source: targetSource,
    uids: [
      {
        id: matchingPpid.id,
        atype: 1,
        ext: {
          stype: "ppuid",
        },
      },
    ],
  };
};

const getUserIds = async () => {
  const ppIds = await getPPIDs();
  const eids = [];

  // Transform SCH IDs to NO format
  const envHashEid = transformPPIDToEid(
    ppIds,
    "SCHSE-EnvHash",
    "SCHNO-EnvHash",
  );
  const userHashEid = transformPPIDToEid(
    ppIds,
    "SCHSE-UserHash",
    "SCHNO-UserHash",
  );

  if (envHashEid) eids.push(envHashEid);
  if (userHashEid) eids.push(userHashEid);

  return [
    {
      name: "pubProvidedId",
      params: {
        eids,
      },
    },
  ];
};

// Create apntag poller (allows SSR/test environments)
const getApnTag = createSDKPoller<AstApnTag>(
  "getApnTag",
  () => window?.apntag,
  { allowSSR: true },
);

const getGenericExternalId = ({
  adId,
  loggedIn,
}: {
  adId?: string;
  loggedIn: boolean;
}): number => {
  if (!adId) return 0;
  if (!loggedIn) return 1;
  return 2;
};

const getPPIDs = async (): Promise<EidsUserSource[]> => {
  const pulse = getPulseTracker();
  try {
    const identifiers = await pulse?.pulseTracker.getAdvertisingIdentifiers();

    if (identifiers?.xandr) {
      const { ppId1, ppId2 } = identifiers.xandr;

      return ppId1
        ? [
            { id: `${ppId1}`, source: "SCHSE-UserHash" },
            { id: `${ppId2}`, source: "SCHSE-EnvHash" },
          ]
        : [{ id: `${ppId2}`, source: "SCHSE-EnvHash" }];
    }
  } catch (e) {
    console.error(
      "getPPIDs: Failed to get advertising identifiers from Pulse",
      e,
    );
  }

  return [];
};

async function getAdId() {
  const pulse = getPulseTracker();
  const identifiers = await pulse?.pulseTracker.getAdvertisingIdentifiers();

  if (identifiers?.xandr) {
    const { adId } = identifiers.xandr;
    return adId;
  }

  return undefined;
}

const getPPIDWithTagValues = async (
  userId?: string,
  isEconomy?: boolean,
): Promise<[EidsUserSource[], Record<string, AstKeywordValue>]> => {
  const pulse = getPulseTracker();

  const environmentId = await pulse?.pulseTracker.getEnvironmentId();
  const ppIds = await getPPIDs();
  const ppid3 = await PPID3CacheHandler(userId, environmentId, isEconomy);

  const ppidTags = { "aa-sch-schuserhash": 0, "aa-sch-schenvhash": 0 };

  if (ppIds?.some((ppid) => ppid.source === "SCHSE-UserHash")) {
    ppidTags["aa-sch-schuserhash"] = 1;
  }

  if (ppIds?.some((ppid) => ppid.source === "SCHSE-EnvHash")) {
    ppidTags["aa-sch-schenvhash"] = 1;
  }

  return [[...ppIds, ...ppid3], ppidTags];
};

const sortedScreens = Object.keys(sharedVars)
  .map((key) => ({ key, value: sharedVars[key] }))
  .filter(({ key }) => /^screen-/.test(key))
  .sort((a, b) => a.value - b.value);

const getCurrentBreakpoint = () => {
  const windowWidth = window.innerWidth;
  return sortedScreens.reduce((a, b) => (b.value <= windowWidth ? b : a), {
    key: "screen-xs-min",
    value: 0,
  }).key;
};

const getLayout = () => layoutNames[getCurrentBreakpoint()];

const getSupplyType = () => {
  const layout = getLayout();
  return `web_${layout === "mobile" ? "phone" : layout}`;
};

const windowKeyWords = () => {
  const entries = new URLSearchParams(window.location.search).entries();
  const acc: { [key: string]: string } = {};
  let kv: IteratorResult<[string, string]>;
  while ((kv = entries.next())) {
    if (kv.done) {
      break;
    }
    const [key, val] = (kv.value || []) as [string?, string?];
    if (val && key?.startsWith("kv_")) {
      acc[key.substring(3)] = val;
    }
  }
  return acc;
};

const sanitizeValue = (val = "") =>
  val
    .replace(specialCharacters, "")
    .replace(/[åä]/g, "a")
    .replace(/[ö]/g, "o")
    .replace(/[ÅÄ]/g, "A")
    .replace(/[Ö]/g, "O")
    .replace(/&/g, "--")
    .replace(/[,/\s]/g, "-");

const sizeToArray = ({ width, height }: { width: number; height: number }) => [
  width,
  height,
];

const isBannerAd = (e: AstAdObj) => e.adType === "banner";

const isNativeAd = (e: AstAdObj) => e.adType === "native";

const getFeedSplitIndex = (context: ViewType, adRules: AdRuleType[]) => {
  const fullscreen = adRules.filter(
    (adRule) =>
      adRule.rules.position === "fullscreen" &&
      adRule.rules.views?.includes(context),
  );
  return fullscreen.length > 0 ? fullscreen[0]?.rules?.index : 10;
};

const removePageContextFromId = (id: string) => {
  return id.replace("-feed", "").replace("-article", "");
};

export {
  getAdId,
  getApnTag,
  getBiddingSdk,
  getCurrentBreakpoint,
  getFeedSplitIndex,
  getGenericExternalId,
  getLayout,
  getPPIDWithTagValues,
  getSupplyType,
  getUserIds,
  isBannerAd,
  isNativeAd,
  removePageContextFromId,
  sanitizeValue,
  sizeToArray,
  windowKeyWords,
};
